import type { UserRole } from '@prisma/client'

interface UserClinic {
  id: string
  slug: string
  name: string
}

interface UserSubscription {
  status: string
  currentPeriodEnd: string | null
  billingInterval: string | null
  cancelAtPeriodEnd: boolean
  hasStripeSubscription: boolean
}

export interface UserProfile {
  id: string
  email: string
  name: string | null
  role: UserRole
  marketingConsent: boolean
  clinics: UserClinic[]
  isPremium: boolean
  isAdmin: boolean
  subscription: UserSubscription | null
}

const ACTIVE_CLINIC_KEY = 'activeClinicSlug'

const user = ref<UserProfile | null>(null)
const pending = ref(false)
const error = ref<string | null>(null)
const activeClinicSlug = ref<string | null>(null)
 
let watcherRegistered = false

// Restore from localStorage on client
if (import.meta.client) {
  activeClinicSlug.value = localStorage.getItem(ACTIVE_CLINIC_KEY)
}

export function useUser() {
  const { isAuthenticated, isAuthReady } = useAuth()

  const role = computed(() => user.value?.role || 'VISITOR')
  const isClinicOwner = computed(() => user.value?.role === 'CLINIC_OWNER')

  const clinics = computed(() => user.value?.clinics ?? [])

  const activeClinic = computed(() => {
    const list = clinics.value
    if (list.length === 0) return null
    if (activeClinicSlug.value) {
      const found = list.find(c => c.slug === activeClinicSlug.value)
      if (found) return found
    }
    return list[0]
  })

  // Alias for backwards compatibility — all existing consumers use `clinic`
  const clinic = activeClinic

  const hasClinic = computed(() => !!activeClinic.value)

  const isPremium = computed(() => user.value?.isPremium ?? false)
  const isAdmin = computed(() => user.value?.isAdmin ?? false)
  const isTrialing = computed(() => user.value?.subscription?.status === 'TRIALING')

  const trialDaysRemaining = computed(() => {
    if (!isTrialing.value || !user.value?.subscription?.currentPeriodEnd) return null
    const end = new Date(user.value.subscription.currentPeriodEnd)
    const now = new Date()
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return Math.max(0, diff)
  })

  const isCanceled = computed(() =>
    user.value?.subscription?.cancelAtPeriodEnd === true
    && user.value?.subscription?.status === 'ACTIVE'
  )

  const isPastDue = computed(() =>
    user.value?.subscription?.status === 'PAST_DUE'
  )

  const setActiveClinic = (slug: string) => {
    activeClinicSlug.value = slug
    if (import.meta.client) {
      localStorage.setItem(ACTIVE_CLINIC_KEY, slug)
    }
  }

  const syncActiveClinic = () => {
    const list = clinics.value
    if (list.length === 0) {
      activeClinicSlug.value = null
      if (import.meta.client) localStorage.removeItem(ACTIVE_CLINIC_KEY)
      return
    }
    const slugValid = activeClinicSlug.value && list.some(c => c.slug === activeClinicSlug.value)
    if (!slugValid) {
      setActiveClinic(list[0]!.slug)
    }
  }

  const fetchUser = async () => {
    if (!isAuthenticated.value) {
      user.value = null
      return
    }

    pending.value = true
    error.value = null

    try {
      const response = await $fetch<{ success: boolean; data: UserProfile }>('/api/me')
      if (response.success) {
        user.value = response.data
        syncActiveClinic()
        useAnalytics().identify(response.data)
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch user'
      user.value = null
    } finally {
      pending.value = false
    }
  }

  const clearUser = () => {
    user.value = null
    error.value = null
  }

  // Auto-fetch when auth state changes (register only once across all useUser() calls)
  if (!watcherRegistered) {
    watcherRegistered = true
    watch(
      () => [isAuthenticated.value, isAuthReady.value],
      ([authenticated, ready]) => {
        if (ready) {
          if (authenticated) {
            fetchUser()
          } else {
            clearUser()
          }
        }
      },
      { immediate: true }
    )
  }

  return {
    user: readonly(user),
    pending: readonly(pending),
    error: readonly(error),
    role,
    isClinicOwner,
    hasClinic,
    clinics,
    clinic,
    isPremium,
    isAdmin,
    isTrialing,
    isCanceled,
    isPastDue,
    trialDaysRemaining,
    setActiveClinic,
    fetchUser,
    clearUser,
  }
}
